import { useTranslation } from 'react-i18next'

type Props = {
  projectName: string
  busy: boolean
  onImportAsNewVersion: () => void
  onCreateNewProject: () => void
  onCancel: () => void
}

/**
 * Shown when an upload's filename matches an existing project. Lets
 * the user choose between importing as a new version of that project
 * (the common case — addresses the "I uploaded the same doc and got
 * a duplicate" report) or creating a separate new project.
 *
 * "Import as new version" is the primary action because the typical
 * intent when re-uploading a file is to track changes against the
 * existing review.
 */
export function UploadConflictDialog({
  projectName,
  busy,
  onImportAsNewVersion,
  onCreateNewProject,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-conflict-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
    >
      <div
        className="w-full max-w-md rounded border p-6"
        style={{
          background: 'var(--color-bg)',
          borderColor: 'var(--color-border)',
        }}
      >
        <h2
          id="upload-conflict-title"
          className="text-xl font-medium mb-2"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('uploadConflict.title', { name: projectName })}
        </h2>
        <p className="mb-6 text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('uploadConflict.body', { name: projectName })}
        </p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onImportAsNewVersion}
            disabled={busy}
            className="rounded px-3 py-2 text-sm"
            style={{
              background: 'var(--color-accent)',
              color: 'var(--color-accent-fg)',
            }}
          >
            {busy ? '…' : t('uploadConflict.importAsNewVersion')}
          </button>
          <button
            type="button"
            onClick={onCreateNewProject}
            disabled={busy}
            className="rounded border px-3 py-2 text-sm"
            style={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-fg)',
              background: 'transparent',
            }}
          >
            {t('uploadConflict.createNew')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-xs underline self-end mt-1"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('uploadConflict.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}